import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles(theme => ({
  paper: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  select: {
    minWidth: 140,
  },
  cell: {
    verticalAlign: "top",
  },
}));

const MODE_FIELDS = [
  { mode: "atendimento", field: "aiPromptId" },
  { mode: "postsale", field: "aiPostsalePromptId" },
  { mode: "aftercare", field: "aiAftercarePromptId" },
];

const ConnectionAiPromptAssign = () => {
  const classes = useStyles();
  const [whatsApps, setWhatsApps] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [waRes, promptRes] = await Promise.all([
        api.get("/whatsapp/"),
        api.get("/ai-prompts"),
      ]);
      setWhatsApps(Array.isArray(waRes.data) ? waRes.data : []);
      setPrompts(Array.isArray(promptRes.data) ? promptRes.data : []);
    } catch (err) {
      toastError(err);
    }
  }, []);

  useEffect(() => {
    load();
    const onChanged = () => load();
    window.addEventListener("ai-prompts-changed", onChanged);
    return () => window.removeEventListener("ai-prompts-changed", onChanged);
  }, [load]);

  const promptsByMode = useMemo(() => {
    const map = {
      atendimento: [],
      postsale: [],
      aftercare: [],
    };
    (prompts || [])
      .filter(p => p.isActive !== false)
      .forEach(p => {
        const mode = p.mode || "atendimento";
        if (map[mode]) map[mode].push(p);
      });
    return map;
  }, [prompts]);

  const handleAssign = async (whatsAppId, field, value) => {
    setSavingId(`${whatsAppId}-${field}`);
    try {
      const payload = { [field]: value || null };
      const { data } = await api.put(`/whatsapp/${whatsAppId}`, payload);
      setWhatsApps(prev =>
        prev.map(w => (w.id === whatsAppId ? { ...w, ...data } : w))
      );
      toast.success(i18n.t("settings.settings.aiPromptAssign.saved"));
    } catch (err) {
      toastError(err);
    } finally {
      setSavingId(null);
    }
  };

  return (
    <Paper className={classes.paper} variant="outlined">
      <Typography variant="h6">
        {i18n.t("settings.settings.aiPromptAssign.title")}
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        {i18n.t("settings.settings.aiPromptAssign.help")}
      </Typography>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>
              {i18n.t("settings.settings.aiPromptAssign.connection")}
            </TableCell>
            {MODE_FIELDS.map(({ mode }) => (
              <TableCell key={mode}>
                {i18n.t(`settings.settings.aiPrompts.modes.${mode}`)}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {whatsApps.map(wa => (
            <TableRow key={wa.id}>
              <TableCell className={classes.cell}>
                <Typography variant="body2">{wa.name}</Typography>
              </TableCell>
              {MODE_FIELDS.map(({ mode, field }) => (
                <TableCell key={field} className={classes.cell}>
                  <FormControl
                    variant="outlined"
                    size="small"
                    className={classes.select}
                    disabled={savingId === `${wa.id}-${field}`}
                  >
                    <InputLabel shrink>
                      {i18n.t(`settings.settings.aiPrompts.modes.${mode}`)}
                    </InputLabel>
                    <Select
                      label={i18n.t(`settings.settings.aiPrompts.modes.${mode}`)}
                      displayEmpty
                      value={wa[field] || ""}
                      onChange={e =>
                        handleAssign(wa.id, field, e.target.value || null)
                      }
                    >
                      <MenuItem value="">
                        {i18n.t("settings.settings.aiPromptAssign.useGlobal")}
                      </MenuItem>
                      {(promptsByMode[mode] || []).map(p => (
                        <MenuItem key={p.id} value={p.id}>
                          {p.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>
              ))}
            </TableRow>
          ))}
          {!whatsApps.length ? (
            <TableRow>
              <TableCell colSpan={4}>
                {i18n.t("settings.settings.aiPromptAssign.empty")}
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
    </Paper>
  );
};

export default ConnectionAiPromptAssign;
